import { useContext, useEffect, useRef, useState } from "react";
import {
    Modal, Sheet, Box, Stack, Typography, Button, IconButton, Chip,
    CircularProgress, LinearProgress, Divider,
} from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "@mui/material";

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import { LayoutContext } from "../../context";
import { APIImportManagerApplications, APIGetImportTemplate } from "../../api";

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 МБ

export default function ModalImportApplications({ open, onClose, onImported }) {
    const { tokenEmployee } = useContext(LayoutContext);
    const isMobile = useMediaQuery('(max-width:768px)');

    const [phase, setPhase] = useState('select'); // 'select' | 'uploading' | 'done' | 'error'
    const [file, setFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [result, setResult] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [templateLoading, setTemplateLoading] = useState(false);

    const inputRef = useRef(null);
    const autoCloseRef = useRef(null);

    useEffect(() => {
        if (open) {
            setPhase('select');
            setFile(null);
            setResult(null);
            setErrorMsg('');
            setDragOver(false);
        }
        return () => { if (autoCloseRef.current) clearTimeout(autoCloseRef.current); };
    }, [open]);

    const handleFiles = (fileList) => {
        const f = fileList && fileList[0];
        if (!f) return;
        const name = (f.name || '').toLowerCase();
        if (!(name.endsWith('.xlsx') || name.endsWith('.xlsm'))) {
            setErrorMsg('Поддерживается только формат .xlsx');
            return;
        }
        if (f.size > MAX_FILE_SIZE) {
            setErrorMsg(`Файл больше ${MAX_FILE_SIZE / (1024 * 1024)} МБ`);
            return;
        }
        setErrorMsg('');
        setFile(f);
    };

    const handleDownloadTemplate = async () => {
        setTemplateLoading(true);
        try {
            const blob = await APIGetImportTemplate(tokenEmployee);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Шаблон_импорта_заявок.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setErrorMsg('Не удалось скачать шаблон');
        } finally {
            setTemplateLoading(false);
        }
    };

    const handleClose = () => {
        if (phase === 'uploading') return;
        if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
        onClose?.();
    };

    const handleStart = async () => {
        if (!file) return;
        setPhase('uploading');
        setErrorMsg('');
        try {
            const res = await APIImportManagerApplications(file, tokenEmployee);
            setResult(res);
            setPhase('done');
            onImported?.(); // обновляем список сразу — часть заявок/черновиков уже создана
            const hasErrors = Array.isArray(res?.errors) && res.errors.length > 0;
            if (!hasErrors) {
                autoCloseRef.current = setTimeout(() => { onClose?.(); }, 3000);
            }
        } catch (err) {
            const data = err?.response?.data;
            setErrorMsg(data?.detail || data?.error || 'Не удалось импортировать файл');
            setPhase('error');
        }
    };

    const hasErrors = Array.isArray(result?.errors) && result.errors.length > 0;

    return (
        <Modal
            open={!!open}
            onClose={handleClose}
            sx={{
                zIndex: 10102,
                display: "flex",
                alignItems: { xs: "flex-end", sm: "center" },
                justifyContent: "center",
            }}
            slotProps={{
                backdrop: { sx: { backdropFilter: "blur(6px)", backgroundColor: "rgba(0,0,0,0.35)" } },
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 30, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 350, damping: 30 }}
                style={{ width: "100%", maxWidth: isMobile ? "100%" : 520, outline: "none", display: "flex" }}
            >
                <Sheet
                    sx={{
                        width: "100%",
                        borderRadius: { xs: "20px 20px 0 0", sm: "22px" },
                        overflow: "hidden",
                        boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
                        border: "1.5px solid", borderColor: "divider",
                        display: "flex", flexDirection: "column",
                        maxHeight: { xs: "92dvh", sm: "88dvh" },
                    }}
                >
                    {/* ── Шапка ── */}
                    <Box sx={{
                        px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box sx={{
                                width: 38, height: 38, borderRadius: "12px",
                                bgcolor: "primary.softBg", color: "primary.500",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <CloudUploadRoundedIcon />
                            </Box>
                            <Typography level="title-lg" sx={{ fontWeight: 700 }}>Импорт заявок</Typography>
                        </Stack>
                        <IconButton variant="plain" color="neutral" onClick={handleClose} disabled={phase === 'uploading'} sx={{ borderRadius: "10px" }}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </Box>

                    {/* ── Тело ── */}
                    <Box sx={{ px: 3, py: 2.5, overflowY: "auto", flex: 1 }}>
                        <AnimatePresence mode="wait">
                            {/* ── Выбор файла ── */}
                            {phase === 'select' && (
                                <motion.div key="select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                    <input
                                        ref={inputRef} type="file" accept=".xlsx,.xlsm" hidden
                                        onChange={(e) => handleFiles(e.target.files)}
                                    />

                                    <Sheet variant="soft" color="primary" sx={{ borderRadius: "12px", p: 1.5, mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <DescriptionRoundedIcon sx={{ fontSize: 22, color: "primary.500" }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography level="body-sm" fontWeight={600}>Не знаете формат?</Typography>
                                            <Typography level="body-xs" color="neutral">Скачайте шаблон и заполните по примеру</Typography>
                                        </Box>
                                        <Button
                                            size="sm" variant="solid" color="primary"
                                            startDecorator={<FileDownloadRoundedIcon sx={{ fontSize: 16 }} />}
                                            loading={templateLoading}
                                            onClick={handleDownloadTemplate}
                                            sx={{ borderRadius: "10px", flexShrink: 0 }}
                                        >
                                            Скачать шаблон
                                        </Button>
                                    </Sheet>

                                    <Box
                                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                                        onClick={() => inputRef.current?.click()}
                                        sx={{
                                            cursor: "pointer",
                                            border: "2px dashed",
                                            borderColor: dragOver ? "primary.400" : (file ? "success.400" : "neutral.300"),
                                            bgcolor: dragOver ? "primary.softBg" : (file ? "success.softBg" : "background.level1"),
                                            borderRadius: "16px",
                                            px: 2, py: 5,
                                            textAlign: "center",
                                            transition: "all 0.15s ease",
                                        }}
                                    >
                                        {file ? (
                                            <Stack alignItems="center" spacing={1}>
                                                <InsertDriveFileRoundedIcon sx={{ fontSize: 40, color: "success.500" }} />
                                                <Typography level="title-sm" sx={{ fontWeight: 600, wordBreak: "break-all" }}>{file.name}</Typography>
                                                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                                    {(file.size / 1024).toFixed(0)} КБ — нажмите, чтобы выбрать другой
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Stack alignItems="center" spacing={1}>
                                                <CloudUploadRoundedIcon sx={{ fontSize: 44, color: dragOver ? "primary.500" : "neutral.400" }} />
                                                <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                                                    Перетащите файл сюда или нажмите
                                                </Typography>
                                                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                                    Формат .xlsx по шаблону столбцов
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>

                                    {errorMsg && (
                                        <Typography level="body-sm" color="danger" sx={{ mt: 1.5 }}>{errorMsg}</Typography>
                                    )}
                                </motion.div>
                            )}

                            {/* ── Загрузка ── */}
                            {phase === 'uploading' && (
                                <motion.div key="uploading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                    <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                                        <CircularProgress size="lg" color="primary" />
                                        <Typography level="title-sm" sx={{ fontWeight: 600 }}>Импортируем заявки…</Typography>
                                        <Typography level="body-xs" sx={{ color: "text.tertiary", textAlign: "center" }}>
                                            Проверяем данные и создаём заявки. Это может занять немного времени.
                                        </Typography>
                                        <LinearProgress color="primary" sx={{ width: "80%" }} />
                                    </Stack>
                                </motion.div>
                            )}

                            {/* ── Готово ── */}
                            {phase === 'done' && !hasErrors && (
                                <motion.div key="ok" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                                    <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
                                        <CheckCircleRoundedIcon sx={{ fontSize: 56, color: "success.500" }} />
                                        <Typography level="title-md" sx={{ fontWeight: 700 }}>
                                            Успешно создано: {result?.created ?? 0}
                                        </Typography>
                                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                            Окно закроется автоматически
                                        </Typography>
                                    </Stack>
                                </motion.div>
                            )}

                            {phase === 'done' && hasErrors && (
                                <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                                        <Chip color="success" variant="soft" startDecorator={<CheckCircleRoundedIcon sx={{ fontSize: 15 }} />}>
                                            Создано: {result?.created ?? 0}
                                        </Chip>
                                        <Chip color="neutral" variant="soft">
                                            Черновиков: {result?.drafts ?? 0}
                                        </Chip>
                                        <Chip color="warning" variant="soft" startDecorator={<ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />}>
                                            С замечаниями: {result.errors.length}
                                        </Chip>
                                    </Stack>

                                    <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 700, mb: 1 }}>
                                        Список правок
                                    </Typography>
                                    <Stack spacing={1}>
                                        {result.errors.map((e, i) => (
                                            <Box key={i} sx={{
                                                border: "1px solid", borderColor: "divider", borderRadius: "12px",
                                                p: 1.25, bgcolor: "background.level1",
                                            }}>
                                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                                                    <Chip size="sm" color={e.is_draft ? "neutral" : "danger"} variant="soft">
                                                        Строка {e.row}
                                                    </Chip>
                                                    {e.is_draft && (
                                                        <Chip size="sm" color="warning" variant="soft">черновик</Chip>
                                                    )}
                                                </Stack>
                                                <Stack spacing={0.25}>
                                                    {(e.messages || []).map((m, j) => (
                                                        <Typography key={j} level="body-xs" sx={{ color: "text.secondary" }}>• {m}</Typography>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </motion.div>
                            )}

                            {/* ── Ошибка (структура файла и т.п.) ── */}
                            {phase === 'error' && (
                                <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                    <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
                                        <ErrorOutlineRoundedIcon sx={{ fontSize: 48, color: "danger.500" }} />
                                        <Typography level="title-sm" sx={{ fontWeight: 600, textAlign: "center" }}>{errorMsg}</Typography>
                                    </Stack>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>

                    {/* ── Подвал ── */}
                    {phase !== 'uploading' && phase !== 'done' && (
                        <Box sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.level1" }}>
                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                {phase === 'error' ? (
                                    <>
                                        <Button variant="plain" color="neutral" onClick={handleClose} sx={{ borderRadius: "12px" }}>
                                            Закрыть
                                        </Button>
                                        <Button variant="solid" color="primary" onClick={() => { setPhase('select'); setErrorMsg(''); }} sx={{ borderRadius: "12px" }}>
                                            Назад
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button variant="plain" color="neutral" onClick={handleClose} sx={{ borderRadius: "12px" }}>
                                            Отмена
                                        </Button>
                                        <Button
                                            variant="solid" color="primary"
                                            onClick={handleStart}
                                            disabled={!file}
                                            startDecorator={<PlayArrowRoundedIcon />}
                                            sx={{ borderRadius: "12px", minWidth: 130, fontWeight: 700 }}
                                        >
                                            Начать
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {phase === 'done' && hasErrors && (
                        <Box sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.level1" }}>
                            <Stack direction="row" justifyContent="flex-end">
                                <Button variant="solid" color="primary" onClick={handleClose} sx={{ borderRadius: "12px", minWidth: 130, fontWeight: 700 }}>
                                    Готово
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Sheet>
            </motion.div>
        </Modal>
    );
}